/**
 * @fileoverview Utility functions for formatting test/assignment status badges.
 * Provides styling and text mapping for different assignment states.
 *
 * @example
 * // Render a status badge for a completed assignment
 * formatTestStatus('complete')
 * // Returns a styled span element showing "Completed"
 *
 * @example
 * // Render a status badge with custom text
 * formatTestStatus('incomplete', 'In Progress')
 * // Returns a styled span element showing "In Progress"
 */


const statusMapStyle: Partial<Record<Novoic.AssignmentStatus, string>> = {
    overdue: 'bg-yellow-100 text-yellow-800',
    not_started: 'bg-gray-100 text-gray-800',
    incomplete: 'bg-indigo-100 text-indigo-800',
    assigned: 'bg-indigo-100 text-indigo-800',
    complete: 'bg-indigo-500 text-white',
    cancelled: 'bg-red-100 text-red-800',
    expired: 'bg-red-100 text-red-800',
    timeout: 'bg-red-100 text-red-800',
    not_eligible: 'bg-red-100 text-red-800',
}

const statusMap: Record<string, string> = {
    not_started: 'Not started',
    incomplete: 'Started',
    assigned: 'Assigned',
    complete: 'Completed',
    cancelled: 'Cancelled',
    timeout: 'Failed',
    expired: 'Failed',
    not_eligible: 'Not eligible',
}

/**
 * @function classNames
 * @description Joins an array of class names into a single string.
 * @param {string[]} classes - The array of class names to join.
 * @returns {string} - The joined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function formatTestStatus
 * @description Formats the test/assignment status.
 * @param {Novoic.AssignmentStatus} status - The status of the test/assignment.
 * @param {string} title - The title of the test/assignment.
 * @returns {JSX.Element} - The formatted test/assignment status.
 */
export function formatTestStatus(status: Novoic.AssignmentStatus, title?: string) {
    return (
        <span
            className={classNames(
                'whitespace-nowrap text-xs font-medium rounded-[10px] py-0.5 px-2',
                statusMapStyle[status]
            )}
        >
            {title || statusMap[status]}
        </span>
    )
}
